import { Breadcrumb, Button, Card, Col, Row, Typography } from "antd";
import { useLoaderData, useNavigate } from "react-router-dom";
import { ListingBrowseView } from "~/components/ListingBrowse";
import {
  breadcrumbHomeItem,
  breadcrumbItem,
  breadcrumbLinkItem,
  TableHeaderBar,
  TableHeaderInfo,
} from "~/components";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { ROUTES } from "~/utils";
import type { PropertyDetailsPageLoaderType } from "./loaders/PropertyDetailsPageLoader";

export const PropertyDetailsPage: React.FC = () => {
  const loaderData = useLoaderData() as PropertyDetailsPageLoaderType;
  const { t } = useAppTranslation();
  const property = loaderData.propertyInfo;

  const address = (() => {
    if (!property) return "";
    const line1 = property.street?.trim();
    const line2 = [
      property.city,
      property.state,
      property.zipCode,
      property.country,
    ]
      .filter(Boolean)
      .join(", ");
    const addressParts = [line1, line2].filter(Boolean);
    if (property.poBox?.trim()) {
      addressParts.push(`${t("PO_Box")}: ${property.poBox.trim()}`);
    }
    return addressParts.join("\n");
  })();

  return (
    <section style={{ marginTop: "2rem" }}>
      <Breadcrumb
        separator=">"
        items={[
          breadcrumbHomeItem,
          breadcrumbLinkItem(ROUTES.unit, t("Listing_browse_title")),
          breadcrumbItem(property?.name ?? t("Listing_property_browse_title_fallback")),
        ]}
      />

      <div style={{ marginTop: "2rem" }}>
      <TableHeaderBar
        left={
          <TableHeaderInfo
            title={t("Listing_property_details_title")}
            count={property?.name}
          />
        }
      />

      {property ? (
        <Card style={{ marginBottom: 24 }}>
          <Typography.Title level={2} style={{ marginTop: 0 }}>
            {property.name}
          </Typography.Title>

          {property.description ? (
            <Typography.Paragraph style={{ marginBottom: 16 }}>
              {property.description}
            </Typography.Paragraph>
          ) : null}

          <Row gutter={[24, 16]}>
            {address ? (
              <Col xs={24} md={12}>
                <Typography.Text strong>{t("Location_Information")}</Typography.Text>
                <Typography.Paragraph
                  style={{ marginTop: 8, marginBottom: 0, whiteSpace: "pre-line" }}
                >
                  {address}
                </Typography.Paragraph>
              </Col>
            ) : null}

            {property.contact?.trim() ? (
              <Col xs={24} md={12}>
                <Typography.Text strong>{t("Contact")}</Typography.Text>
                <Typography.Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                  {property.contact}
                </Typography.Paragraph>
              </Col>
            ) : null}

            {property.areaSize?.trim() ? (
              <Col xs={24} md={12}>
                <Typography.Text strong>{t("Area_Size")}</Typography.Text>
                <Typography.Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                  {property.areaSize} sq ft
                </Typography.Paragraph>
              </Col>
            ) : null}
          </Row>

          {property.amenities.length > 0 ? (
            <section style={{ marginTop: 20 }}>
              <Typography.Title level={4} style={{ marginBottom: 12 }}>
                {t("Listing_details_amenities_title")}
              </Typography.Title>
              <Row gutter={[16, 16]}>
                {property.amenities.map((amenity) => (
                  <Col key={amenity.id} xs={24} sm={12} md={8}>
                    <Card size="small" title={amenity.name}>
                      {amenity.locationDescription ? (
                        <Typography.Text type="secondary">
                          {amenity.locationDescription}
                        </Typography.Text>
                      ) : null}
                      {amenity.description ? (
                        <Typography.Paragraph
                          style={{ marginTop: 8, marginBottom: 0 }}
                        >
                          {amenity.description}
                        </Typography.Paragraph>
                      ) : null}
                    </Card>
                  </Col>
                ))}
              </Row>
            </section>
          ) : null}
        </Card>
      ) : null}

      <ListingBrowseView loaderData={loaderData} />
      </div>
    </section>
  );
};
